import { Tooltip } from '@affine/component';
import {
  type CustomLottieProps,
  InternalLottie,
} from '@affine/component/internal-lottie';
import { useI18n } from '@affine/i18n';
import type { HTMLAttributes } from 'react';
import type React from 'react';
import { cloneElement, useState } from 'react';

import elsterIconWhiteUrl from '../../desktop/dialogs/setting/workspace-setting/integration/erica/elster-icon-white.assets.svg';
import databaseHover from './animation-data/database-hover.json';
import edgelessHover from './animation-data/edgeless-hover.json';
import pageHover from './animation-data/page-hover.json';

type HoverAnimateControllerProps = {
  active?: boolean;
  hide?: boolean;
  trash?: boolean;
  children: React.ReactElement<CustomLottieProps>;
} & HTMLAttributes<HTMLDivElement>;

type ModeTooltipSide = 'top' | 'right' | 'bottom' | 'left';

const HoverAnimateController = ({
  children,
  ...props
}: HoverAnimateControllerProps) => {
  const [startAnimate, setStartAnimate] = useState(false);
  return (
    <div
      onMouseEnter={() => setStartAnimate(true)}
      onMouseLeave={() => setStartAnimate(false)}
      {...props}
    >
      {cloneElement(children, {
        isStopped: !startAnimate,
        speed: 1,
        width: 20,
        height: 20,
      })}
    </div>
  );
};

const pageLottieOptions = {
  loop: false,
  autoplay: false,
  animationData: pageHover,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};

const edgelessLottieOptions = {
  loop: false,
  autoplay: false,
  animationData: edgelessHover,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};

const databaseLottieOptions = {
  loop: false,
  autoplay: false,
  animationData: databaseHover,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};

export const PageSwitchItem = (
  props: Omit<HoverAnimateControllerProps, 'children'> & {
    disabledReason?: string;
    showTooltip?: boolean;
    tooltipSide?: ModeTooltipSide;
  }
) => {
  const t = useI18n();
  const {
    disabledReason,
    showTooltip = true,
    tooltipSide = 'bottom',
    ...controllerProps
  } = props;
  const item = (
    <HoverAnimateController {...controllerProps}>
      <InternalLottie options={pageLottieOptions} />
    </HoverAnimateController>
  );
  return showTooltip || disabledReason ? (
    <Tooltip
      content={disabledReason ?? t['com.streamlain.header.mode-switch.page']()}
      shortcut={disabledReason ? undefined : ['$alt', 'Q']} // sl-df0: Alt+Q for Page
      side={tooltipSide}
    >
      {item}
    </Tooltip>
  ) : (
    item
  );
};

export const EdgelessSwitchItem = (
  props: Omit<HoverAnimateControllerProps, 'children'> & {
    showTooltip?: boolean;
    tooltipSide?: ModeTooltipSide;
  }
) => {
  const t = useI18n();
  const {
    showTooltip = true,
    tooltipSide = 'bottom',
    ...controllerProps
  } = props;
  const item = (
    <HoverAnimateController {...controllerProps}>
      <InternalLottie options={edgelessLottieOptions} />
    </HoverAnimateController>
  );
  return showTooltip ? (
    <Tooltip
      content={t['com.streamlain.header.mode-switch.edgeless']()}
      shortcut={['$alt', 'E']} // sl-df0: Alt+E for Edgeless
      side={tooltipSide}
    >
      {item}
    </Tooltip>
  ) : (
    item
  );
};

// sl-2w9: Simple hover controller for non-Lottie icons
type SimpleHoverControllerProps = {
  children: React.ReactNode;
} & HTMLAttributes<HTMLDivElement>;

const SimpleHoverController = ({
  children,
  ...props
}: SimpleHoverControllerProps) => {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 20,
        height: 20,
        transform: isHovered ? 'scale(1.1)' : 'scale(1)',
        transition: 'transform 0.15s ease-in-out',
      }}
      {...props}
    >
      {children}
    </div>
  );
};

// sl-yphr.25: ELSTER mode switch item (only visible for ELSTER docs)
export const ElsterSwitchItem = (
  props: Omit<SimpleHoverControllerProps, 'children'>
) => {
  return (
    <Tooltip content="ELSTER" shortcut={['$alt', 'T']} side="bottom">
      <SimpleHoverController {...props}>
        <img
          src={elsterIconWhiteUrl}
          width={16}
          height={16}
          alt="ELSTER"
          style={{ display: 'inline-block' }}
        />
      </SimpleHoverController>
    </Tooltip>
  );
};

// sl-a7z: Fullscreen mode switch item (always active, shows placeholder if no DB)
export interface FullscreenSwitchItemProps extends Omit<
  SimpleHoverControllerProps,
  'children'
> {
  disabledReason?: string;
  showTooltip?: boolean;
  tooltipSide?: ModeTooltipSide;
}

export const FullscreenSwitchItem = ({
  disabledReason,
  showTooltip = true,
  tooltipSide = 'bottom',
  ...props
}: FullscreenSwitchItemProps) => {
  const t = useI18n();
  const item = (
    <HoverAnimateController {...props}>
      <InternalLottie options={databaseLottieOptions} />
    </HoverAnimateController>
  );
  return showTooltip || disabledReason ? (
    <Tooltip
      content={
        disabledReason ?? t['com.streamlain.header.mode-switch.database']()
      }
      shortcut={disabledReason ? undefined : ['$alt', 'D']} // sl-a7z: Alt+D for Database Fullscreen
      side={tooltipSide}
    >
      {item}
    </Tooltip>
  ) : (
    item
  );
};
